import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CarryOutOutlined, DeleteOutlined, EditOutlined, ExpandAltOutlined, PlusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Tree, Form, Row, Col, Typography, Select, Input, Space, Button, Flex, message, Popconfirm, Modal } from 'antd';
import { assignLevelToElements, extractOptionsFromTree, getTopLevelInFormData, addCurrentNodeToParent, removeNodeFromTree, getNodeParent, checkDuplicatesAtLevel, getNodeId } from '../../utils/extractElementsFromTree';
import { getOrgStructure, getStructureDefinition, saveOrgStructure } from '../../services/OrgStructure.js';
import { useSelector } from 'react-redux';
import { showLoader } from '../../utils/showloader.js';
import { v4 as uuidv4 } from "uuid";
import { trimStringValues } from '../../utils/trimStrings';

const Structure = ({ handleAddBtn, adminEntitlements }) => {
    const { t } = useTranslation();
    const [elements, setElements] = useState([])
    const [orgId, setOrgId] = useState(null)
    const [originalTreeData, setOriginalTreeData] = useState([])
    const [showAddStructure, setShowAddStructure] = useState(false)
    const [orgStructure, setOrgStructure] = useState([])
    const [expandKeys, setExpandedKeys] = useState([])
    const [expandAll, setExpandAll] = useState(false);
    const [maxLevel, setMaxLevel] = useState(0)
    const [levelOptions, setLevelOptions] = useState({})
    const [form] = Form.useForm();
    const values = Form.useWatch([], form);
    const [isEdit, setIsEdit] = useState(false)
    const [selectedNode, setSelectedNode] = useState({})
    const [expandTree, setExpandTree] = useState(false)
    const { userInfo } = useSelector(state => state.user)

    useEffect(() => {
        if (values && values.elementLabel) {
            const labelInElements = elements.find(element => element.define === values.elementLabel)

            if (labelInElements.level > 0) {
                let structureOptions = extractOptionsFromTree(originalTreeData, elements, labelInElements)
                Object.keys(structureOptions).forEach((key, index) => {
                    let currentLevel = "level" + (index - 1)
                    if (key !== "level0" && values[currentLevel]) {
                        let selectedElement = structureOptions[key].filter(opt => opt.parentValue === values[currentLevel])
                        structureOptions[key] = selectedElement
                    }
                })
                setLevelOptions({ ...structureOptions })
                setMaxLevel(labelInElements.level)
            } else {
                setLevelOptions({})
                setMaxLevel(0)
            }
        }
    }, [values])

    useEffect(() => {
        if (isEdit && Object.keys(levelOptions).length > 0) {
            let levelsObj = { level0: "" }
            let nodeLevel = selectedNode.level.split("").pop()
            if (Number(nodeLevel) > 0) {

                let parentObj = { ...selectedNode.parent }
                let nodeOptions = { ...levelOptions }
                let levels = Object.keys(nodeOptions).reverse().filter(opt => {
                    let currentLevel = opt.split("").pop();
                    return Number(currentLevel) < Number(nodeLevel)

                });

                for (let i = 0; i < levels.length; i++) {
                    let key = levels[i]
                    let curArray = nodeOptions[key];
                    let reqObj = curArray.find(level => level.value === parentObj.value)
                    levelsObj[key] = reqObj.value
                    if (i < levels.length - 1) {
                        parentObj = nodeOptions[levels[i + 1]].find(opt => opt.value === reqObj.parentValue)
                    }
                }

                form.setFieldsValue({ elementLabel: selectedNode.label, elementName: selectedNode.value, ...levelsObj })
            }
            setIsEdit(false)
        }
    }, [levelOptions])

    const fetchOrgElements = useCallback(async () => {
        let orgResponse = await getStructureDefinition(userInfo);
        if (orgResponse.status === 200 && orgResponse.data) {
            let updatedElements = assignLevelToElements(orgResponse.data)
            setElements(updatedElements)
        }
    }, [])

    const onSelect = (selectedKeys, info) => {
    };

    const fetchStructure = useCallback(async () => {
        let strResponse = await getOrgStructure(userInfo);
        if (strResponse?.data?.[0]?.orgStructure) {
            setOrgId(strResponse.data[0].orgId)
            let treeData = JSON.parse(strResponse.data[0].orgStructure)
            setOriginalTreeData(treeData)
        }
    }, [])

    useEffect(() => {
        fetchStructure();
    }, [fetchStructure]);

    useEffect(() => {
        fetchOrgElements();
    }, [fetchOrgElements])

    const updateOrgStructure = async (updatedTree) => {
        let postData = { orgStructure: [...updatedTree] }
        if (updatedTree.length > 0) {
            postData.orgId = orgId
            postData.tenantId = localStorage.getItem("tenantId")
        }
        showLoader(true)
        let saveResponse = await saveOrgStructure(postData, originalTreeData.length > 0 ? true : false, userInfo);
        showLoader(false)
        if (saveResponse.status === 200) {
            message.success(t('Structure updated successfully'))
            setOrgId(saveResponse.data.orgId)
            setOriginalTreeData(saveResponse.data.orgStructure)
            form.resetFields();
            setShowAddStructure(!showAddStructure)

        } else {
            message.error(saveResponse.message)
        }
        // axios({
        //     method: originalTreeData.length === 0 ? "POST" : "PUT",
        //     url: `${BACKEND_API}/org`,
        //     headers: {
        //         userId: 1,
        //     },
        //     data: {
        //         tenantId: "1",
        //         orgStructure: JSON.stringify(updatedTree)
        //     }
        // })
        //     .then((response) => {
        //         message.success(t('Structure updated successfully'))
        //         setOriginalTreeData(JSON.parse(response.data.orgStructure))
        //         form.resetFields();
        //         handleAddBtn()
        //     })
        //     .catch((error) => {
        //         message.error(error)
        //     });
    }

    const deleteNode = (node) => {
        let updatedTrees = [...originalTreeData]
        if (node.level === "level0") {
            updatedTrees = updatedTrees.filter(data => data.value !== node.value)
        } else {
            removeNodeFromTree(updatedTrees, node)
        }
        updateOrgStructure(updatedTrees)
    }

    const editNode = (node) => {
        form.resetFields();
        let formFields = { id: node.id, elementLabel: node?.label, elementName: node?.value, elementCode: node?.elementCode || "", abbreviation: node?.abbreviation || "" }
        if (node.level !== 'level1' && node.level !== 'level0') {
            formFields[node.parent.level] = node.parent.value
        }
        form.setFieldsValue(formFields)
        setShowAddStructure(true)

        setSelectedNode({ ...node })
        setIsEdit(true);
    }

    const titleContent = (node) => {
        return (
            <Flex className='tree-title-container' justify="space-between" align="center" style={{ height: "36px" }}>
                <Flex style={{ height: "36px" }}>
                    <div className='element-label'>
                        <div>{node.label}</div>
                    </div>
                    <div className='element-name'>
                        <span>{node.elementCode ? node.elementCode + " " : ""}
                            {node.value}
                            {node.abbreviation ? "  " + node.abbreviation : ""}

                        </span>
                    </div>
                </Flex>
                <div>
                    {
                        // adminEntitlements.includes("Organisation_Structure_Update") &&
                        <Button size="small" style={{ marginRight: "5px" }} icon={<EditOutlined />} onClick={() => editNode(node)} />

                    }
                    {
                        (!node.children || node.children.length === 0) &&
                        // adminEntitlements.includes("Organisation_Structure_Update") &&
                        <Popconfirm
                            title={t("Delete the component")}
                            description={t("Are you sure to delete this component?")}
                            okText={t("Yes")}
                            cancelText={t("No")}
                            placement='leftTop'
                            onConfirm={() => deleteNode(node)}
                        >
                            <Button size="small" style={{ marginRight: "5px" }} icon={<DeleteOutlined />} />
                        </Popconfirm>
                    }
                </div>
            </Flex>
        )
    }

    const childrenMapper = (node) => {
        let keys = []
        let formattedChildren = node.children.map((child, index) => {
            let reqChild = { ...child, parent: { ...node, key: `${node.value}-${child.value}` } }
            let childObj = { title: titleContent(reqChild), key: `${node.value}-${child.value}`, icon: <PlusCircleOutlined /> }
            keys.push(`${node.label}-${node.value}`)
            setExpandedKeys([...expandKeys, ...keys])
            if (child.children && child.children.length > 0) {
                childObj.children = childrenMapper(child)
            }
            childObj.parent = node
            return childObj
        })
        return formattedChildren
    }

    const dataTreeMapper = (data) => {
        let orgData = [...data], defaultKeys = []
        orgData = orgData.map((node, index) => {
            let nodeObj = { title: titleContent({ ...node }), key: `${node.label}-${node.value}`, icon: <CarryOutOutlined /> }
            defaultKeys.push(`${node.label}-${node.value}`)
            if (node.children && node.children.length > 0) {
                nodeObj.children = childrenMapper({ ...node })
            }
            return nodeObj
        })
        setExpandedKeys([...expandKeys, ...defaultKeys])
        return orgData
    }

    useEffect(() => {
        let formattedTreeData = dataTreeMapper(originalTreeData)
        setOrgStructure(formattedTreeData)
    }, [originalTreeData])

    const saveStructure = (values) => {
        values = trimStringValues(values)
        // return
        let reqKey = getTopLevelInFormData(values)
        let updatedTrees = [...originalTreeData];
        if (reqKey === "top") {
            let blExists = updatedTrees.find(node => (node.value === values.elementName.trim()) || (node.value?.toLowerCase() === values.elementName?.toLowerCase()))
            if (blExists && blExists.id !== selectedNode?.id) {
                message.error(t('BL with the same name already exists'))
                return
            }
            if (selectedNode.value) {
                updatedTrees = updatedTrees.map(tree => {
                    let treeObj = { ...tree }
                    if (tree.id === selectedNode.id) {
                        treeObj.label = values.elementLabel
                        treeObj.value = values.elementName
                        treeObj.abbreviation = values.abbreviation
                        treeObj.elementCode = values.elementCode
                    }
                    return treeObj
                })
            } else {
                let id = uuidv4();
                updatedTrees.push({
                    label: values.elementLabel, value: values.elementName.trim(), elementCode: values.elementCode || "",
                    abbreviation: values.abbreviation || "", children: [], level: "level0", id: id
                })
            }

        } else {
            // findNode based on level and add the current node as a child in that parent node
            // findNode based on level and add the current node as a child in that parent node
            let elementExistsInTheLevel = checkDuplicatesAtLevel(updatedTrees, reqKey, values)
            if (elementExistsInTheLevel?.exists) {
                if ((selectedNode.value && selectedNode.id !== elementExistsInTheLevel?.existedElement?.id) || !selectedNode.value)
                    message.error(`${values.elementLabel} with the same name already exists in the same level`);
                return
            }

            for (let i = 0; i < updatedTrees.length; i++) {
                let node = updatedTrees[i];
                // updatedTrees.forEach((node, index) => {
                if (node.level === reqKey && node.value === values[reqKey]) {
                    let key = "level" + (Number(reqKey.slice(-1)) + 1)
                    if (selectedNode.value) {
                        node.children = node.children.map(child => {
                            let childObj = { ...child }
                            if (child.id === selectedNode.id && node.value === selectedNode?.parent?.value) {
                                childObj.label = values.elementLabel
                                childObj.value = values.elementName.trim()
                                childObj.elementCode = values.elementCode || ""
                                childObj.abbreviation = values.abbreviation || ""
                            }
                            return childObj
                        })
                        // return
                    } else {
                        let id = uuidv4();
                        node.children.push({ label: values.elementLabel, value: values.elementName.trim(), elementCode: values.elementCode || "", abbreviation: values.abbreviation || "", children: [], level: key, id: id })
                    }
                } else {
                    addCurrentNodeToParent(node, values, reqKey, selectedNode, elements)
                }
            }

        }
        updateOrgStructure(updatedTrees)
    }

    const handleAddComponent = () => {
        form.resetFields()
        setSelectedNode({})
        setShowAddStructure(true)
        setMaxLevel(0)
        setLevelOptions({})
    }

    const onCancel = () => {
        form.resetFields()
        setShowAddStructure(false)

    }

    return (
        <div style={{ background: "#fff", padding: "1.5rem", borderRadius: "1rem", marginTop: "1.5rem", minHeight: "50vh" }}>
            <Flex justify="space-between" align="center" style={{ margin: "0 0 1.5rem 0" }}>

                <Typography.Title level={4} style={{ padding: 0, margin: 0 }} >
                    {t("Structure")}
                </Typography.Title>
                <Flex gap={16} align='center'>
                    <Button size="large" icon={<ExpandAltOutlined />} />
                    <Button size="large" className="submit-button" icon={<PlusOutlined />} onClick={handleAddComponent}>{t('Add Component')}</Button>
                </Flex>

            </Flex>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "normal",
                }}
            >
                <div style={{ width: "70%", paddingRight: "12px" }}>
                    {
                        orgStructure.length > 0 ?
                            <>
                                {/* <div className='cursor-pointer' style={{ color: "#1677ff", marginBottom: "10px", textDecoration: "underline", textAlign: "right" }} onClick={() => setExpandAll(!expandAll)}>{expandAll ? t("Collapse All") : t("Expand All")}</div> */}
                                <Tree key={expandAll}
                                    showLine={true}
                                    showIcon={false}
                                    onSelect={onSelect}
                                    treeData={orgStructure}
                                    style={{ width: "100%" }}
                                    titleHeight={30}
                                    defaultExpandAll={expandAll}
                                />
                            </>
                            : null
                    }
                </div>
                {
                    showAddStructure ?
                        <div style={{ width: "30%", padding: "12px", borderLeft: "1px solid rgba(0, 0, 0, 0.1)", textAlign: "left", position: "relative" }}>
                            <Row gutter={8}>
                                <Col span={24}>
                                    <Typography.Title level={4} style={{ marginTop: "0px" }}>
                                        {t("Component Details")}
                                    </Typography.Title>
                                </Col>

                            </Row>
                            <Form
                                layout="vertical"
                                form={form}
                                name="rule-form"
                                onFinish={saveStructure}
                            >
                                <Form.Item name="elementLabel" rules={[{
                                    required: true,
                                    message: t('Component is required')
                                }]} label={t("Select Organisation Component")} style={{ marginBottom: "15px" }}>
                                    <Select options={elements.map(element => {
                                        return { label: element.define, value: element.define }
                                    })} />
                                </Form.Item>
                                <Form.Item name="elementName" rules={[{
                                    required: true,
                                    whitespace: true,
                                    message: t('Element name is required')
                                }]} label={t("Element Name")} style={{ marginBottom: "15px" }}>
                                    <Input placeholder={t("Element Name")} />
                                </Form.Item>
                                <Form.Item name="elementCode" label={t("Element Code")} style={{ marginBottom: "15px" }}>
                                    <Input placeholder={t("Element Code")} />
                                </Form.Item>
                                <Form.Item name="abbreviation" label={t("Abbreviation")} style={{ marginBottom: "15px" }}>
                                    <Input placeholder={t("Abbreviation")} />
                                </Form.Item>
                                {
                                    [...Array(maxLevel)].map((e, i) => {
                                        let levelKey = "level" + i;
                                        let reqOptions = levelOptions["level" + i]
                                        return <>
                                            <Form.Item name={"level" + i} rules={[{
                                                required: true,
                                                message: t('Required')
                                            }]} label={t("Select") + " " + (reqOptions[0]?.define || "")} style={{ marginBottom: "15px" }}>
                                                <Select options={reqOptions} />
                                            </Form.Item>
                                        </>
                                    })
                                }
                                <Space>
                                    <Form.Item>
                                        <Button type="primary" className='submit-button' htmlType="submit">{t("Save")}</Button>
                                    </Form.Item>
                                    <Form.Item>
                                        <Button onClick={onCancel}>{t("Cancel")}</Button>
                                    </Form.Item>
                                </Space>
                            </Form>
                        </div>
                        : null
                }
            </div>

            <Modal title="" open={expandTree} onOk={() => setExpandTree(false)} onCancel={() => setExpandTree(false)} >
                <div style={{ width: "90%", margin: "5%", minHeight: "90%" }}>
                    <Tree key={expandAll}
                        showLine={true}
                        showIcon={false}
                        onSelect={onSelect}
                        treeData={orgStructure}
                        style={{ width: "100%" }}
                        titleHeight={30}
                        defaultExpandAll={true}
                    />
                </div>
            </Modal>
        </div>
    )
}

export default Structure